import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { showSuccessAlert } from 'actions';
import {
  upsertCustomerContract,
  upsertCustomerContractFailure,
  upsertCustomerContractsuccess,
} from 'actions/customerContracts';
import {
  fetchIndexHealthSample,
  fetchIndexHealthSampleFailure,
  fetchIndexHealthSampleRequest,
  fetchIndexHealthSampleSuccess,
  saveSOWUsersAndImport,
  saveSOWUsersAndImportFailure,
  saveSOWUsersAndImportRequest,
  saveSOWUsersAndImportSuccess,
} from 'actions/customers';
import { saveUsers, saveUsersFailure, saveUsersSuccess } from 'actions/users';
import { apiClient as LitLingoClient } from 'client';
import { push } from 'connected-react-router';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import { call, put, select, take, takeLatest } from 'redux-saga/effects';
import { getCustomerDomain, getUserCustomer } from 'selectors/auth';
import { getNavParamsByResource } from 'selectors/nav';
import { API, ErrorObject, SagaReturn } from 'types';
import { reverse } from 'utils/urls';

function* fetchIndexHealthSampleSaga(): SagaReturn {
  yield put(fetchIndexHealthSampleRequest());

  const resourceParams = (yield select(
    getNavParamsByResource(resourceQueryParamName.envelopes)
  )) as ReturnType<ReturnType<typeof getNavParamsByResource>>;

  const response = (yield call([LitLingoClient.resources.customers.extras, 'indexHealthSample'], {
    params: { ...resourceParams },
  })) as API.Response<API.Customers.IndexHealthSample>;
  if (response.error != null) {
    yield put(fetchIndexHealthSampleFailure(response.error));
  } else {
    yield put(fetchIndexHealthSampleSuccess(response.data));
  }
}

function* saveSOWUsersAndImportSaga(action: ReturnType<typeof saveSOWUsersAndImport>): SagaReturn {
  const { payload } = action;
  const customer = (yield select(getUserCustomer)) as ReturnType<typeof getUserCustomer>;

  yield put(saveSOWUsersAndImportRequest());

  if (payload.users.length > 0) {
    yield put(saveUsers({ users: payload.users, fromPermissionsPage: true }));
    const returnedAction = (yield take([
      saveUsersSuccess.toString(),
      saveUsersFailure.toString(),
    ])) as ReturnType<ActionCreatorWithPayload<ErrorObject>>;

    if (returnedAction.type === saveUsersFailure.toString()) {
      yield put(saveSOWUsersAndImportFailure(returnedAction.payload));
      return;
    }
  }

  yield put(upsertCustomerContract({ contract: { ...payload.sow } }));
  const returnedAction = (yield take([
    upsertCustomerContractsuccess.toString(),
    upsertCustomerContractFailure.toString(),
  ])) as ReturnType<ActionCreatorWithPayload<ErrorObject>>;

  if (returnedAction.type === upsertCustomerContractFailure.toString()) {
    yield put(saveSOWUsersAndImportFailure(returnedAction.payload));
    return;
  }

  const response = (yield call([LitLingoClient.resources.tasks.extras, 'sweepO365'], {
    data: { customer_uuid: customer?.uuid || '' },
  })) as API.Response<API.Customers.IndexHealthSample>;
  if (response.error != null) {
    yield put(saveSOWUsersAndImportFailure(response.error));
  } else {
    yield put(saveSOWUsersAndImportSuccess());
    yield put(showSuccessAlert('Import data kickoff successfully queued up.'));

    const customerDomain = (yield select(getCustomerDomain)) as string;
    const path = reverse({
      routeName: 'customer-contracts',
      customerDomain,
    });
    yield put(push(path));
  }
}

function* customersSaga(): SagaReturn {
  yield takeLatest(fetchIndexHealthSample, fetchIndexHealthSampleSaga);
  yield takeLatest(saveSOWUsersAndImport, saveSOWUsersAndImportSaga);
}

export default customersSaga;
